import { useState, useCallback } from 'react'
import { OVERRIDES_STORAGE_KEY } from '@/config/admin'
import { MODULES as DEFAULT_MODULES } from '@/data/modules'
import type { Module } from '@/types'

export type ModuleOverride = Partial<Pick<
  Module,
  | 'title'
  | 'tagline'
  | 'overview'
  | 'steps'
  | 'keyTalkingPoints'
  | 'pricingNote'
  | 'cadence'
  | 'heroAngle'
  | 'realWorldExample'
  | 'additionalServices'
>>

export interface ContentOverrides {
  modules: Record<string, ModuleOverride>
}

function loadOverrides(): ContentOverrides {
  try {
    const raw = localStorage.getItem(OVERRIDES_STORAGE_KEY)
    if (!raw) return { modules: {} }
    return JSON.parse(raw) as ContentOverrides
  } catch {
    return { modules: {} }
  }
}

function saveOverrides(overrides: ContentOverrides) {
  try {
    localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(overrides))
  } catch { /* silent */ }
}

/** Merge default modules with any stored overrides */
export function getMergedModules(overrides: ContentOverrides): Module[] {
  return DEFAULT_MODULES.map(mod => {
    const override = overrides.modules[mod.id]
    if (!override) return mod
    return { ...mod, ...override }
  })
}

export function useContentOverrides() {
  const [overrides, setOverrides] = useState<ContentOverrides>(loadOverrides)

  const updateModule = useCallback((moduleId: string, patch: ModuleOverride) => {
    setOverrides(prev => {
      const next: ContentOverrides = {
        modules: {
          ...prev.modules,
          [moduleId]: { ...(prev.modules[moduleId] ?? {}), ...patch },
        },
      }
      saveOverrides(next)
      return next
    })
  }, [])

  const resetModule = useCallback((moduleId: string) => {
    setOverrides(prev => {
      const { [moduleId]: _removed, ...rest } = prev.modules
      const next: ContentOverrides = { modules: rest }
      saveOverrides(next)
      return next
    })
  }, [])

  const resetAll = useCallback(() => {
    const next: ContentOverrides = { modules: {} }
    saveOverrides(next)
    setOverrides(next)
  }, [])

  const exportJSON = useCallback(() => {
    const blob = new Blob([JSON.stringify(overrides, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = '365hound-content-overrides.json'
    a.click()
    URL.revokeObjectURL(url)
  }, [overrides])

  const importJSON = useCallback((json: string) => {
    try {
      const parsed = JSON.parse(json) as ContentOverrides
      saveOverrides(parsed)
      setOverrides(parsed)
      return true
    } catch {
      return false
    }
  }, [])

  const hasOverride = useCallback(
    (moduleId: string) => !!overrides.modules[moduleId],
    [overrides],
  )

  return {
    overrides,
    updateModule,
    resetModule,
    resetAll,
    exportJSON,
    importJSON,
    hasOverride,
    mergedModules: getMergedModules(overrides),
  }
}
